const EFFECTIVE_DATE = '7 May 2026';
const CONTACT_EMAIL = 'hello@2players.io';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl font-semibold text-white mb-3">{title}</h2>
      <div className="text-gray-300 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="py-12 sm:py-20 max-w-3xl mx-auto">
      <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-pink-500 via-violet-500 to-blue-500 text-transparent bg-clip-text">
        Privacy Policy
      </h1>
      <p className="text-gray-300 mb-10">Effective {EFFECTIVE_DATE}</p>

      <Section title="Who we are">
        <p>
          2Players ("we", "us") operates 2players.io and the 2Players mobile app (together, the "Service"). This policy
          explains what information we collect, why, and what choices you have. Questions? Email{' '}
          <a className="text-pink-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <Section title="What we collect">
        <p>We collect only what we need to run the Service:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong className="text-white">Account data</strong> — email address, chosen username, and (for
            email/password sign-ups) a password hash. If you sign in with Google, we receive your email and basic
            profile information from Google.
          </li>
          <li>
            <strong className="text-white">Profile data</strong> — avatar, optional public profile fields, and friend
            list.
          </li>
          <li>
            <strong className="text-white">Gameplay data</strong> — game results, ELO rating, match history, in-game
            chat messages, and friend requests.
          </li>
          <li>
            <strong className="text-white">Voice / video / text chat</strong> — when you choose to use the in-game chat
            features, audio and video are streamed peer-to-peer between players using WebRTC. We do not record or store
            this content. Text chat messages are stored to deliver them to your opponent.
          </li>
          <li>
            <strong className="text-white">Technical data</strong> — IP address, device type, browser, and basic
            telemetry needed to run the Service securely (e.g. rate limiting, abuse prevention).
          </li>
        </ul>
      </Section>

      <Section title="How we use it">
        <ul className="list-disc pl-6 space-y-2">
          <li>To create and secure your account.</li>
          <li>To match you with opponents and run real-time gameplay.</li>
          <li>To show leaderboards and public profiles.</li>
          <li>To deliver friend requests, invites, and rematch flows.</li>
          <li>To detect and prevent fraud, cheating, and abuse.</li>
          <li>To meet legal obligations.</li>
        </ul>
        <p>We do not sell your personal data, and we do not use it to serve advertising.</p>
      </Section>

      <Section title="Who we share with">
        <p>We rely on a small number of vetted providers to operate the Service:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong className="text-white">Google Firebase</strong> — authentication, Firestore database, and hosting
            infrastructure.
          </li>
          <li>
            <strong className="text-white">Google Sign-In</strong> — optional OAuth sign-in.
          </li>
          <li>
            <strong className="text-white">Cloudflare</strong> — web hosting and content delivery.
          </li>
        </ul>
        <p>
          These providers process data on our behalf under their own privacy terms. We do not sell or rent your data to
          third parties.
        </p>
      </Section>

      <Section title="Cookies and local storage">
        <p>
          We use cookies and browser local storage to keep you signed in, remember preferences, and operate the
          Service. We do not use third-party advertising or tracking cookies.
        </p>
      </Section>

      <Section title="Camera and microphone">
        <p>
          The in-game video and voice chat features only access your camera and microphone when you explicitly turn
          them on. You can revoke this access at any time from your browser or device settings. We do not record audio
          or video.
        </p>
      </Section>

      <Section title="Children">
        <p>
          The Service is not directed to children under 13 (or under 16 in the EEA / UK), and we do not knowingly
          collect personal information from them. If you believe a child has provided us with personal information,
          contact us and we will delete it.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          Depending on where you live, you may have the right to access, correct, export, or delete your personal data,
          and to object to or restrict our processing of it. You can exercise these rights by emailing{' '}
          <a className="text-pink-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          . You can also delete your account from your profile page; this removes your account and associated profile
          data.
        </p>
      </Section>

      <Section title="Data retention">
        <p>
          We keep your account data for as long as your account is active. When you delete your account we remove your
          profile data and disassociate match records from your identity. We may retain limited information where
          required for security, fraud prevention, or legal compliance.
        </p>
      </Section>

      <Section title="Security">
        <p>
          We use industry-standard measures (encryption in transit, authentication via Firebase, access controls) to
          protect your data. No system is perfectly secure, so we cannot guarantee absolute security.
        </p>
      </Section>

      <Section title="International transfers">
        <p>
          Our infrastructure providers may process data in the United States and other countries. Where required, we
          rely on standard contractual clauses or equivalent safeguards.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          We will update this policy when our practices change. The "Effective" date at the top reflects the latest
          version. Material changes will be communicated in-app or by email.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          For privacy questions or to exercise your rights, email{' '}
          <a className="text-pink-400 hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </div>
  );
}
